import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { api, type QuotePortConfig } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'

type RunningStatus = 'running' | 'not_running' | 'unknown'
type TransportType = 'OPEN' | 'ENCLOSED'

type VehicleForm = {
  year: string
  make: string
  model: string
  vin: string
}

const emptyVehicle: VehicleForm = { year: '', make: '', model: '', vin: '' }

const emptyForm = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  pickupAddress1: '',
  pickupAddress2: '',
  pickupCity: '',
  pickupState: '',
  pickupZip: '',
  deliveryAddress1: '',
  deliveryAddress2: '',
  deliveryCity: '',
  deliveryState: '',
  deliveryZip: '',
  requestedPickupDate: '',
  requestedDeliveryDate: '',
  pickupWindowStart: '',
  pickupWindowEnd: '',
  deliveryWindowStart: '',
  deliveryWindowEnd: '',
  runningStatus: 'unknown' as RunningStatus,
  transportType: 'OPEN' as TransportType,
  notes: '',
  consentAccepted: false,
  requestVinPhotoLater: false,
}

const inputClass = 'w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/20'

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="grid gap-1.5 text-sm text-slate-200">
    <span className="font-medium">{label}</span>
    {hint && <span className="text-xs text-slate-400">{hint}</span>}
    {children}
  </label>
}

export default function QuotePortPage() {
  const { accountSlug } = useParams<{ accountSlug: string }>()
  const [config, setConfig] = useState<QuotePortConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [vehicles, setVehicles] = useState<VehicleForm[]>([{ ...emptyVehicle }])
  const [routeMiles, setRouteMiles] = useState<number | null>(null)
  const [routeSource, setRouteSource] = useState<string | null>(null)
  const [routeLoading, setRouteLoading] = useState(false)

  useEffect(() => {
    if (!accountSlug) return
    setLoading(true)
    api.quotePort.config(accountSlug)
      .then((response) => setConfig(response.data.config))
      .catch((err) => setError(err?.response?.data?.error ?? 'Quote page not found.'))
      .finally(() => setLoading(false))
  }, [accountSlug])

  const vinStatusHint = useMemo(() => {
    const vin = (vehicles[0]?.vin ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!vin) return form.requestVinPhotoLater ? 'VIN missing. Photo capture can be requested later.' : 'VIN optional.'
    if (vin.length === 17) return 'VIN looks ready for decode and verification.'
    return 'VIN will be flagged for verification.'
  }, [form.requestVinPhotoLater, vehicles])

  const update = (key: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const pickupZipLookup = useZipLocation({
    zip: form.pickupZip,
    onCity: (city) => update('pickupCity', city),
    onState: (state) => update('pickupState', state),
    lookupZip: api.geo.publicZipLookup,
  })
  const deliveryZipLookup = useZipLocation({
    zip: form.deliveryZip,
    onCity: (city) => update('deliveryCity', city),
    onState: (state) => update('deliveryState', state),
    lookupZip: api.geo.publicZipLookup,
  })

  const updateVehicle = (index: number, key: keyof VehicleForm, value: string) => {
    setVehicles((prev) => prev.map((vehicle, vehicleIndex) => vehicleIndex === index ? { ...vehicle, [key]: value } : vehicle))
  }

  const calculateRoute = async () => {
    if (form.pickupZip.trim().length !== 5 || form.deliveryZip.trim().length !== 5) {
      setError('Enter a 5-digit pickup ZIP and drop-off ZIP first.')
      return
    }
    setRouteLoading(true)
    setError(null)
    try {
      const response = await api.quotePort.mileagePreview(accountSlug!, {
        originZip: form.pickupZip,
        destinationZip: form.deliveryZip,
        originCity: form.pickupCity,
        originState: form.pickupState,
        destinationCity: form.deliveryCity,
        destinationState: form.deliveryState,
      })
      setRouteMiles(response.data.estDistanceMiles ?? null)
      setRouteSource(response.data.mileageSource ?? response.data.mileageProvider ?? null)
    } catch (err: any) {
      setRouteMiles(null)
      setRouteSource(null)
      setError(err?.response?.data?.error ?? 'We could not calculate the route distance. Check both ZIP codes and try again.')
    } finally {
      setRouteLoading(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSuccess(null)

    if (!form.customerName.trim() || !form.customerPhone.trim() || !form.customerEmail.trim()) {
      setError('Customer name, phone, and email are required.')
      return
    }
    if (!form.pickupCity.trim() || !form.pickupState.trim() || !form.pickupZip.trim()) {
      setError('Pickup city, state, and zip are required.')
      return
    }
    if (!form.deliveryCity.trim() || !form.deliveryState.trim() || !form.deliveryZip.trim()) {
      setError('Delivery city, state, and zip are required.')
      return
    }
    if (vehicles.some((vehicle) => !vehicle.year.trim() || !vehicle.make.trim() || !vehicle.model.trim())) {
      setError('Add a year, make, and model for every vehicle, or remove the unused vehicle row.')
      return
    }
    if (!form.consentAccepted) {
      setError('Consent is required.')
      return
    }

    setSubmitting(true)
    try {
      const response = await api.quotePort.submit(accountSlug!, {
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim(),
        pickupAddress1: form.pickupAddress1.trim() || undefined,
        pickupAddress2: form.pickupAddress2.trim() || undefined,
        pickupCity: form.pickupCity.trim(),
        pickupState: form.pickupState.trim(),
        pickupZip: form.pickupZip.trim(),
        deliveryAddress1: form.deliveryAddress1.trim() || undefined,
        deliveryAddress2: form.deliveryAddress2.trim() || undefined,
        deliveryCity: form.deliveryCity.trim(),
        deliveryState: form.deliveryState.trim(),
        deliveryZip: form.deliveryZip.trim(),
        requestedPickupDate: form.requestedPickupDate || undefined,
        requestedDeliveryDate: form.requestedDeliveryDate || undefined,
        pickupWindowStart: form.pickupWindowStart || undefined,
        pickupWindowEnd: form.pickupWindowEnd || undefined,
        deliveryWindowStart: form.deliveryWindowStart || undefined,
        deliveryWindowEnd: form.deliveryWindowEnd || undefined,
        vehicles: vehicles.map((vehicle) => ({
          year: vehicle.year ? Number(vehicle.year) : undefined,
          make: vehicle.make.trim(),
          model: vehicle.model.trim(),
          vin: vehicle.vin.trim() || undefined,
        })),
        vehicleYear: vehicles[0]?.year ? Number(vehicles[0].year) : undefined,
        vehicleMake: vehicles[0]?.make.trim() || undefined,
        vehicleModel: vehicles[0]?.model.trim() || undefined,
        vin: vehicles[0]?.vin.trim() || undefined,
        runningStatus: form.runningStatus,
        transportType: form.transportType,
        notes: form.notes.trim() || undefined,
        consentAccepted: form.consentAccepted,
        requestVinPhotoLater: form.requestVinPhotoLater,
      })

      setSuccess(`Quote request submitted. Reference ${response.data.quoteRequest.id}`)
      setForm(emptyForm)
      setVehicles([{ ...emptyVehicle }])
      setRouteMiles(null)
      setRouteSource(null)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to submit quote request.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center text-sm text-slate-400">Loading QuotePort…</div>
  }

  if (!config) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center text-sm text-red-300">{error ?? 'Quote page not found.'}</div>
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl shadow-black/30 overflow-hidden">
          <div className="border-b border-white/10 px-6 py-6 sm:px-8 sm:py-8" style={{ borderTopColor: config.primaryColor ?? undefined }}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.32em] text-cyan-200/80">QuotePort</div>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold text-white">{config.quotePageHeadline ?? `Request a quote from ${config.companyName}`}</h1>
                <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-300">Hosted quote intake for Diamond Dispatch. Submit the move details now and add VIN verification later if needed.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
                <div className="font-semibold text-white">{config.companyName}</div>
                {config.phone && <div className="mt-1">{config.phone}</div>}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="grid gap-5 px-6 py-6 sm:px-8 sm:py-8">
            {error && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</div>}
            {success && <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{success}</div>}

            <section className="grid gap-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4 sm:p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">Customer</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <input className="rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.customerName} onChange={(e) => update('customerName', e.target.value)} placeholder="Customer name" />
                <input className="rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.customerPhone} onChange={(e) => update('customerPhone', e.target.value)} placeholder="Phone" />
                <input className="sm:col-span-2 rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} placeholder="Email" />
              </div>
            </section>

            <section className="grid gap-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4 sm:p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">Pickup & Delivery</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="grid gap-3 rounded-xl border border-white/10 bg-slate-950/50 p-3">
                  <div><div className="text-xs uppercase tracking-wide text-cyan-200">Pickup location</div><p className="mt-1 text-xs text-slate-400">Where the vehicle will be collected.</p></div>
                  <Field label="Pickup address"><input aria-label="Pickup address" className={inputClass} value={form.pickupAddress1} onChange={(e) => update('pickupAddress1', e.target.value)} /></Field>
                  <Field label="Pickup address line 2"><input aria-label="Pickup address line 2" className={inputClass} value={form.pickupAddress2} onChange={(e) => update('pickupAddress2', e.target.value)} /></Field>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Pickup city"><input aria-label="Pickup city" className={inputClass} value={form.pickupCity} onChange={(e) => update('pickupCity', e.target.value)} /></Field>
                    <Field label="Pickup state"><input aria-label="Pickup state" className={`${inputClass} uppercase`} value={form.pickupState} onChange={(e) => update('pickupState', e.target.value.toUpperCase())} maxLength={2} /></Field>
                    <Field label="Pickup ZIP"><input aria-label="Pickup ZIP" className={inputClass} value={form.pickupZip} onChange={(e) => update('pickupZip', normalizeZipInput(e.target.value))} inputMode="numeric" maxLength={10} /></Field>
                  </div>
                </div>

                <div className="grid gap-3 rounded-xl border border-white/10 bg-slate-950/50 p-3">
                  <div><div className="text-xs uppercase tracking-wide text-emerald-200">Drop-off location</div><p className="mt-1 text-xs text-slate-400">Where the vehicle will be delivered.</p></div>
                  <Field label="Drop-off address"><input aria-label="Drop-off address" className={inputClass} value={form.deliveryAddress1} onChange={(e) => update('deliveryAddress1', e.target.value)} /></Field>
                  <Field label="Drop-off address line 2"><input aria-label="Drop-off address line 2" className={inputClass} value={form.deliveryAddress2} onChange={(e) => update('deliveryAddress2', e.target.value)} /></Field>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Drop-off city"><input aria-label="Drop-off city" className={inputClass} value={form.deliveryCity} onChange={(e) => update('deliveryCity', e.target.value)} /></Field>
                    <Field label="Drop-off state"><input aria-label="Drop-off state" className={`${inputClass} uppercase`} value={form.deliveryState} onChange={(e) => update('deliveryState', e.target.value.toUpperCase())} maxLength={2} /></Field>
                    <Field label="Drop-off ZIP"><input aria-label="Drop-off ZIP" className={inputClass} value={form.deliveryZip} onChange={(e) => update('deliveryZip', normalizeZipInput(e.target.value))} inputMode="numeric" maxLength={10} /></Field>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <div><div className="font-semibold text-cyan-100">Pickup date & window</div><p className="mt-1 text-xs text-slate-400">Tell us when the vehicle can be collected.</p></div>
                  <Field label="Pickup date"><input type="date" className={inputClass} value={form.requestedPickupDate} onChange={(e) => update('requestedPickupDate', e.target.value)} /></Field>
                  <div className="grid gap-3 sm:grid-cols-2"><Field label="Earliest pickup time"><input type="time" className={inputClass} value={form.pickupWindowStart} onChange={(e) => update('pickupWindowStart', e.target.value)} /></Field><Field label="Latest pickup time"><input type="time" className={inputClass} value={form.pickupWindowEnd} onChange={(e) => update('pickupWindowEnd', e.target.value)} /></Field></div>
                  <Field label="Pickup notes" hint="Gate codes, contact instructions, or timing details"><textarea className={`${inputClass} min-h-20`} value={form.notes} onChange={(e) => update('notes', e.target.value)} /></Field>
                </div>
                <div className="grid gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <div><div className="font-semibold text-emerald-100">Drop-off date & window</div><p className="mt-1 text-xs text-slate-400">Tell us when the destination can receive it.</p></div>
                  <Field label="Drop-off date"><input type="date" className={inputClass} value={form.requestedDeliveryDate} onChange={(e) => update('requestedDeliveryDate', e.target.value)} /></Field>
                  <div className="grid gap-3 sm:grid-cols-2"><Field label="Earliest drop-off time"><input type="time" className={inputClass} value={form.deliveryWindowStart} onChange={(e) => update('deliveryWindowStart', e.target.value)} /></Field><Field label="Latest drop-off time"><input type="time" className={inputClass} value={form.deliveryWindowEnd} onChange={(e) => update('deliveryWindowEnd', e.target.value)} /></Field></div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
                <div><div className="font-medium text-white">Route distance</div><div className="text-xs text-slate-400">Uses the pickup and drop-off ZIP codes.</div></div>
                <button type="button" onClick={calculateRoute} disabled={routeLoading} className="rounded-lg border border-cyan-300/40 px-3 py-2 text-sm font-semibold text-cyan-100 hover:bg-cyan-400/10 disabled:opacity-50">{routeLoading ? 'Calculating…' : 'Calculate distance'}</button>
                <div className="text-right text-sm text-slate-200">{routeMiles != null ? <><span className="font-semibold text-white">{routeMiles.toLocaleString()} mi</span><span className="ml-2 text-xs text-slate-400">{routeSource}</span></> : 'Distance not calculated'}</div>
                {(pickupZipLookup.loading || deliveryZipLookup.loading || pickupZipLookup.message || deliveryZipLookup.message) && <p className="basis-full text-xs text-slate-400" role="status">{pickupZipLookup.loading || deliveryZipLookup.loading ? 'Looking up ZIP…' : pickupZipLookup.message ?? deliveryZipLookup.message}</p>}
              </div>
            </section>

            <section className="grid gap-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3"><div><h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">Vehicles</h2><p className="mt-1 text-xs text-slate-400">Add every vehicle that should be included in this quote.</p></div><button type="button" onClick={() => setVehicles((prev) => [...prev, { ...emptyVehicle }])} className="rounded-lg border border-cyan-300/40 px-3 py-2 text-sm font-semibold text-cyan-100 hover:bg-cyan-400/10">+ Add vehicle</button></div>
              <div className="grid gap-4">
                {vehicles.map((vehicle, index) => <div key={index} className="grid gap-3 rounded-xl border border-white/10 bg-slate-950/50 p-3">
                  <div className="flex items-center justify-between"><div className="font-medium text-white">Vehicle {index + 1}</div>{vehicles.length > 1 && <button type="button" onClick={() => setVehicles((prev) => prev.filter((_, vehicleIndex) => vehicleIndex !== index))} className="text-xs text-rose-200 hover:text-rose-100">Remove</button>}</div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Year"><input aria-label={`Vehicle ${index + 1} year`} className={inputClass} value={vehicle.year} onChange={(e) => updateVehicle(index, 'year', e.target.value)} inputMode="numeric" /></Field>
                    <Field label="Make"><input aria-label={`Vehicle ${index + 1} make`} className={inputClass} value={vehicle.make} onChange={(e) => updateVehicle(index, 'make', e.target.value)} /></Field>
                    <Field label="Model"><input aria-label={`Vehicle ${index + 1} model`} className={inputClass} value={vehicle.model} onChange={(e) => updateVehicle(index, 'model', e.target.value)} /></Field>
                    <Field label="VIN (optional)"><input aria-label={`Vehicle ${index + 1} VIN`} className={`${inputClass} uppercase`} value={vehicle.vin} onChange={(e) => updateVehicle(index, 'vin', e.target.value.toUpperCase())} maxLength={20} /></Field>
                  </div>
                </div>)}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="rounded-xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-slate-200">
                  <span className="block text-xs uppercase tracking-wide text-slate-400">Running status</span>
                  <select className="mt-1 w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white" value={form.runningStatus} onChange={(e) => update('runningStatus', e.target.value)}>
                    <option value="unknown">Unknown</option>
                    <option value="running">Running</option>
                    <option value="not_running">Not running</option>
                  </select>
                </label>
                <label className="rounded-xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-slate-200">
                  <span className="block text-xs uppercase tracking-wide text-slate-400">Transport type</span>
                  <select className="mt-1 w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white" value={form.transportType} onChange={(e) => update('transportType', e.target.value)}>
                    <option value="OPEN">Open</option>
                    <option value="ENCLOSED">Enclosed</option>
                  </select>
                </label>
              </div>
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-50">{vinStatusHint}</div>
              <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-950/50 px-3 py-3 text-sm text-slate-200">
                <input type="checkbox" checked={form.requestVinPhotoLater} onChange={(e) => update('requestVinPhotoLater', e.target.checked)} className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900" />
                <span>Add VIN photo later if needed. We will keep the request open for future VINtelligent capture.</span>
              </label>
            </section>

            <section className="grid gap-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4 sm:p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">Consent</h2>
              <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-950/50 px-3 py-3 text-sm text-slate-200">
                <input type="checkbox" checked={form.consentAccepted} onChange={(e) => update('consentAccepted', e.target.checked)} className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900" />
                <span>I agree that Diamond Dispatch can contact me about this quote request and use the information above to prepare a transport quote.</span>
              </label>
              {config.termsText && <div className="rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-xs leading-relaxed text-slate-300">{config.termsText}</div>}
            </section>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-400">Hosted by Diamond Dispatch for {config.companyName}. No login required.</div>
              <button type="submit" disabled={submitting} className="inline-flex items-center justify-center rounded-xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-300 disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Submit quote request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
